// #tag: ADMIN OPPORTUNITIES MANAGEMENT SCREEN
// #purpose: Content management interface for administering career recruitments, eligibility criteria, vacancy breakdowns, and stage milestones.
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Briefcase,
  Building2,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Plus,
  Star,
  TrendingUp,
} from 'lucide-react';
import {
  createAdminOpportunity,
  fetchAdminOpportunity,
  fetchAdminOpportunities,
  patchAdminOpportunity,
  type AdminOpportunityRow,
} from '../../api/admin';
import { ApiError } from '../../api/client';
import {
  AdminDetailCard,
  AdminEmptyState,
  AdminKpiStrip,
  AdminPageLayout,
  AdminSearchInput,
  AdminStatusBadge,
  AdminTablePanel,
} from '../../components/admin/AdminPageLayout';
import { LoadingState, ErrorState } from '../../components/states/StateViews';

const CATEGORY_LABELS: Record<string, string> = {
  government_jobs: 'Government jobs',
  entrance_exams: 'Entrance exams',
  admissions: 'Admissions',
  scholarships: 'Scholarships',
  fellowships: 'Fellowships',
  internships: 'Internships',
  apprenticeships: 'Apprenticeships',
  research: 'Research',
};

function categoryLabel(category: string) {
  return CATEGORY_LABELS[category] ?? category.replace(/_/g, ' ');
}

function CreateOpportunityModal({
  open,
  onClose,
  categories,
  qualifications,
}: {
  open: boolean;
  onClose: () => void;
  categories: string[];
  qualifications: string[];
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    title: '',
    organization: '',
    category: categories[0] ?? 'government_jobs',
    qualification: qualifications[0] ?? 'graduate',
    state: 'All India',
    applicationStart: '',
    applicationEnd: '',
    status: 'open',
    isFeatured: false,
    isTrending: false,
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm({
      title: '',
      organization: '',
      category: categories[0] ?? 'government_jobs',
      qualification: qualifications[0] ?? 'graduate',
      state: 'All India',
      applicationStart: '',
      applicationEnd: '',
      status: 'open',
      isFeatured: false,
      isTrending: false,
    });
    setError('');
  }, [open, categories, qualifications]);

  const create = useMutation({
    mutationFn: () =>
      createAdminOpportunity({
        title: form.title.trim(),
        organization: form.organization.trim(),
        category: form.category,
        qualification: form.qualification,
        state: form.state,
        applicationStart: form.applicationStart || null,
        applicationEnd: form.applicationEnd || null,
        status: form.status,
        isFeatured: form.isFeatured,
        isTrending: form.isTrending,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-opportunities'] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Failed to create opportunity');
    },
  });

  if (!open) return null;

  return (
    <div className="wfx-admin-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="wfx-admin-modal glass-surface"
        role="dialog"
        aria-labelledby="create-opportunity-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="wfx-admin-modal-head">
          <div>
            <p className="wfx-admin-breadcrumb">New listing</p>
            <h2 id="create-opportunity-title"><Briefcase size={20} /> Create opportunity</h2>
          </div>
        </header>
        <form
          className="wfx-admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            setError('');
            create.mutate();
          }}
        >
          <label className="wfx-admin-field">
            <span>Title</span>
            <input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
          </label>
          <label className="wfx-admin-field">
            <span>Organization</span>
            <input required value={form.organization} onChange={(e) => setForm((f) => ({ ...f, organization: e.target.value }))} />
          </label>
          <div className="wfx-admin-form-row">
            <label className="wfx-admin-field">
              <span>Category</span>
              <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{categoryLabel(cat)}</option>
                ))}
              </select>
            </label>
            <label className="wfx-admin-field">
              <span>Qualification</span>
              <select value={form.qualification} onChange={(e) => setForm((f) => ({ ...f, qualification: e.target.value }))}>
                {qualifications.map((q) => (
                  <option key={q} value={q}>{q}</option>
                ))}
              </select>
            </label>
          </div>
          <label className="wfx-admin-field">
            <span>State / region</span>
            <input value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))} />
          </label>
          <div className="wfx-admin-form-row">
            <label className="wfx-admin-field">
              <span>Application start</span>
              <input type="date" value={form.applicationStart} onChange={(e) => setForm((f) => ({ ...f, applicationStart: e.target.value }))} />
            </label>
            <label className="wfx-admin-field">
              <span>Application end</span>
              <input type="date" value={form.applicationEnd} onChange={(e) => setForm((f) => ({ ...f, applicationEnd: e.target.value }))} />
            </label>
          </div>
          <label className="wfx-admin-field">
            <span>Status</span>
            <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
              <option value="open">Open</option>
              <option value="closing_soon">Closing soon</option>
              <option value="closed">Closed</option>
            </select>
          </label>
          <label className="wfx-admin-checkbox">
            <input type="checkbox" checked={form.isFeatured} onChange={(e) => setForm((f) => ({ ...f, isFeatured: e.target.checked }))} />
            Feature on homepage
          </label>
          <label className="wfx-admin-checkbox">
            <input type="checkbox" checked={form.isTrending} onChange={(e) => setForm((f) => ({ ...f, isTrending: e.target.checked }))} />
            Mark as trending
          </label>
          {error && <p className="wfx-admin-form-error">{error}</p>}
          <div className="wfx-admin-modal-actions">
            <button type="button" className="secondary-action" onClick={onClose}>Cancel</button>
            <button type="submit" className="primary-action" disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Create opportunity'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function OpportunityDetailCard({
  opportunityId,
  categories,
  qualifications,
  onClose,
}: {
  opportunityId: string | null;
  categories: string[];
  qualifications: string[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-opportunity', opportunityId],
    queryFn: () => fetchAdminOpportunity(opportunityId!),
    enabled: Boolean(opportunityId),
  });

  const update = useMutation({
    mutationFn: (body: Partial<AdminOpportunityRow>) => patchAdminOpportunity(opportunityId!, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-opportunities'] });
      void qc.invalidateQueries({ queryKey: ['admin-opportunity', opportunityId] });
    },
  });

  const row = data?.opportunity;

  return (
    <AdminDetailCard
      open={Boolean(opportunityId)}
      onClose={onClose}
      ariaLabel="Opportunity details"
      breadcrumb="Vacancy listing"
      title={row?.title ?? 'Loading…'}
      illustration="admin.module.opportunities"
      subtitle={
        row ? (
          <>
            <AdminStatusBadge status={row.status} />
            {row.isFeatured && (
              <span className="wfx-admin-opp-flag">
                <Star size={14} aria-hidden="true" /> Featured
              </span>
            )}
            {row.isTrending && (
              <span className="wfx-admin-opp-flag">
                <TrendingUp size={14} aria-hidden="true" /> Trending
              </span>
            )}
          </>
        ) : undefined
      }
    >
      {isLoading && <LoadingState label="Loading opportunity…" />}
      {isError && <ErrorState message="Could not load opportunity." onRetry={() => void refetch()} />}

      {row && (
        <>
          <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
            <div><dt>Organization</dt><dd>{row.organization}</dd></div>
            <div><dt>Category</dt><dd>{categoryLabel(row.category)}</dd></div>
            <div><dt>Qualification</dt><dd>{row.qualification}</dd></div>
            <div><dt>Slug</dt><dd>{row.slug}</dd></div>
            <div>
              <dt>Public page</dt>
              <dd>
                <Link to={`/opportunities/${row.slug}`} className="secondary-action compact" target="_blank">
                  View listing
                  <ExternalLink size={12} aria-hidden="true" />
                </Link>
              </dd>
            </div>
            <div>
              <dt>Deadline</dt>
              <dd>{row.applicationEnd ? new Date(row.applicationEnd).toLocaleDateString() : '—'}</dd>
            </div>
          </dl>

          <div className="wfx-admin-drawer-columns">
            <div className="wfx-admin-drawer-section">
              <h3>Listing details</h3>
              <div className="wfx-admin-form">
                <label className="wfx-admin-field">
                  <span>Title</span>
                  <input
                    defaultValue={row.title}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value && value !== row.title) update.mutate({ title: value });
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Organization</span>
                  <input
                    defaultValue={row.organization}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value && value !== row.organization) update.mutate({ organization: value });
                    }}
                  />
                </label>
                <div className="wfx-admin-form-row">
                  <label className="wfx-admin-field">
                    <span>Category</span>
                    <select
                      defaultValue={row.category}
                      onChange={(e) => update.mutate({ category: e.target.value })}
                    >
                      {categories.map((cat) => (
                        <option key={cat} value={cat}>{categoryLabel(cat)}</option>
                      ))}
                    </select>
                  </label>
                  <label className="wfx-admin-field">
                    <span>Status</span>
                    <select
                      defaultValue={row.status}
                      onChange={(e) => update.mutate({ status: e.target.value })}
                    >
                      <option value="open">Open</option>
                      <option value="closing_soon">Closing soon</option>
                      <option value="closed">Closed</option>
                    </select>
                  </label>
                </div>
                <div className="wfx-admin-form-row">
                  <label className="wfx-admin-field">
                    <span>Qualification</span>
                    <select
                      defaultValue={row.qualification}
                      onChange={(e) => update.mutate({ qualification: e.target.value })}
                    >
                      {qualifications.map((q) => (
                        <option key={q} value={q}>{q}</option>
                      ))}
                    </select>
                  </label>
                  <label className="wfx-admin-field">
                    <span>State</span>
                    <input
                      defaultValue={row.state ?? ''}
                      onBlur={(e) => {
                        const value = e.target.value.trim();
                        if (value !== (row.state ?? '')) update.mutate({ state: value });
                      }}
                    />
                  </label>
                </div>
                <div className="wfx-admin-form-row">
                  <label className="wfx-admin-field">
                    <span>Vacancies</span>
                    <input
                      type="number"
                      defaultValue={row.vacancies ?? ''}
                      onBlur={(e) => {
                        const value = e.target.value ? Number(e.target.value) : null;
                        if (value !== row.vacancies) update.mutate({ vacancies: value });
                      }}
                    />
                  </label>
                  <label className="wfx-admin-field">
                    <span>Salary</span>
                    <input
                      defaultValue={row.salary ?? ''}
                      onBlur={(e) => {
                        const value = e.target.value.trim();
                        if (value !== (row.salary ?? '')) update.mutate({ salary: value || null });
                      }}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="wfx-admin-drawer-section">
              <h3>Dates & links</h3>
              <div className="wfx-admin-form">
                <div className="wfx-admin-form-row">
                  <label className="wfx-admin-field">
                    <span>Application start</span>
                    <input
                      type="date"
                      defaultValue={row.applicationStart?.slice(0, 10) ?? ''}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value !== (row.applicationStart?.slice(0, 10) ?? '')) {
                          update.mutate({ applicationStart: value || null });
                        }
                      }}
                    />
                  </label>
                  <label className="wfx-admin-field">
                    <span>Application end</span>
                    <input
                      type="date"
                      defaultValue={row.applicationEnd?.slice(0, 10) ?? ''}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value !== (row.applicationEnd?.slice(0, 10) ?? '')) {
                          update.mutate({ applicationEnd: value || null });
                        }
                      }}
                    />
                  </label>
                </div>
                <label className="wfx-admin-field">
                  <span>Exam date</span>
                  <input
                    type="date"
                    defaultValue={row.examDate?.slice(0, 10) ?? ''}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (value !== (row.examDate?.slice(0, 10) ?? '')) {
                        update.mutate({ examDate: value || null });
                      }
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Apply URL</span>
                  <input
                    defaultValue={row.officialApplyUrl ?? ''}
                    placeholder="https://"
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (row.officialApplyUrl ?? '')) {
                        update.mutate({ officialApplyUrl: value || null });
                      }
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Official website</span>
                  <input
                    defaultValue={row.officialWebsite ?? ''}
                    placeholder="https://"
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (row.officialWebsite ?? '')) {
                        update.mutate({ officialWebsite: value || null });
                      }
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Description</span>
                  <textarea
                    rows={4}
                    className="wfx-admin-textarea"
                    defaultValue={row.description ?? ''}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (row.description ?? '')) {
                        update.mutate({ description: value || null });
                      }
                    }}
                  />
                </label>
              </div>

              <h3>Promotion</h3>
              <div className="wfx-admin-result-toggle-grid">
                <button
                  type="button"
                  className={`chip-pill${row.isFeatured ? ' is-active' : ''}`}
                  disabled={update.isPending}
                  onClick={() => update.mutate({ isFeatured: !row.isFeatured })}
                >
                  <Star size={14} aria-hidden="true" /> Featured
                </button>
                <button
                  type="button"
                  className={`chip-pill${row.isTrending ? ' is-active' : ''}`}
                  disabled={update.isPending}
                  onClick={() => update.mutate({ isTrending: !row.isTrending })}
                >
                  <TrendingUp size={14} aria-hidden="true" /> Trending
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </AdminDetailCard>
  );
}

function OpportunityCard({
  row,
  onSelect,
}: {
  row: AdminOpportunityRow;
  onSelect: () => void;
}) {
  return (
    <button type="button" className="wfx-admin-opportunity-card glass-surface" onClick={onSelect}>
      <div className="wfx-admin-result-card-head">
        <AdminStatusBadge status={row.status} />
        <div className="wfx-admin-opp-badges">
          {row.isFeatured && <Star size={14} aria-hidden="true" />}
          {row.isTrending && <TrendingUp size={14} aria-hidden="true" />}
        </div>
      </div>
      <strong>{row.title}</strong>
      <span className="wfx-muted wfx-admin-result-org">
        <Building2 size={14} aria-hidden="true" /> {row.organization}
      </span>
      <div className="wfx-admin-page-card-stats">
        <span>{categoryLabel(row.category)}</span>
        <span>{row.qualification}</span>
        {row.applicationEnd && (
          <span>
            <Calendar size={14} aria-hidden="true" />
            {new Date(row.applicationEnd).toLocaleDateString()}
          </span>
        )}
      </div>
    </button>
  );
}

export function AdminOpportunitiesScreen() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [featuredFilter, setFeaturedFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, categoryFilter, statusFilter, featuredFilter]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-opportunities', debouncedQuery, categoryFilter, statusFilter, featuredFilter, page],
    queryFn: () =>
      fetchAdminOpportunities({
        q: debouncedQuery || undefined,
        category: categoryFilter,
        status: statusFilter,
        featured: featuredFilter,
        page,
        limit: 12,
      }),
  });

  const rows = data?.data ?? [];
  const stats = data?.stats;
  const categories = data?.categories ?? Object.keys(CATEGORY_LABELS);
  const qualifications = data?.qualifications ?? ['graduate'];
  const pagination = data?.pagination;

  const kpiItems = useMemo(
    () => [
      { label: 'Total listings', value: stats?.total ?? 0 },
      { label: 'Open', value: stats?.open ?? 0 },
      { label: 'Featured', value: stats?.featured ?? 0 },
      { label: 'Trending', value: stats?.trending ?? 0 },
    ],
    [stats],
  );

  if (isLoading && !data) return <LoadingState label="Loading opportunities…" />;
  if (isError) return <ErrorState message="Could not load opportunities." onRetry={() => void refetch()} />;

  return (
    <>
      <AdminPageLayout
        breadcrumb="Admin / Opportunities"
        title="Opportunities"
        description="Create and manage jobs, exams, admissions, scholarships, and other vacancy listings."
        icon={Briefcase}
        illustration="admin.module.opportunities"
        actions={(
          <button type="button" className="primary-action compact" onClick={() => setCreateOpen(true)}>
            <Plus size={16} aria-hidden="true" />
            New opportunity
          </button>
        )}
      >
        <AdminKpiStrip items={kpiItems} />

        <div className="wfx-admin-blog-grid">
          {rows.map((row) => (
            <OpportunityCard key={row.id} row={row} onSelect={() => setSelectedId(row.id)} />
          ))}
        </div>
        {rows.length === 0 && <AdminEmptyState message="No opportunities match your filters." />}

        <AdminTablePanel
          title="All opportunities"
          toolbar={(
            <div className="wfx-admin-toolbar">
              <AdminSearchInput
                value={query}
                onChange={setQuery}
                placeholder="Search title, organization, or slug…"
              />
              <select
                className="wfx-admin-select"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                aria-label="Filter by category"
              >
                <option value="all">All categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{categoryLabel(cat)}</option>
                ))}
              </select>
              <select
                className="wfx-admin-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Filter by status"
              >
                <option value="all">All status</option>
                <option value="open">Open</option>
                <option value="closing_soon">Closing soon</option>
                <option value="closed">Closed</option>
              </select>
              <select
                className="wfx-admin-select"
                value={featuredFilter}
                onChange={(e) => setFeaturedFilter(e.target.value)}
                aria-label="Filter by featured"
              >
                <option value="all">All listings</option>
                <option value="true">Featured only</option>
                <option value="false">Not featured</option>
              </select>
            </div>
          )}
          footer={
            pagination && pagination.pages > 1 ? (
              <div className="wfx-admin-pagination">
                <button
                  type="button"
                  className="secondary-action compact"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft size={16} aria-hidden="true" />
                  Previous
                </button>
                <span className="wfx-muted">
                  Page {pagination.page} of {pagination.pages} · {pagination.total} listings
                </span>
                <button
                  type="button"
                  className="secondary-action compact"
                  disabled={page >= pagination.pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                  <ChevronRight size={16} aria-hidden="true" />
                </button>
              </div>
            ) : undefined
          }
        >
          <table className="wfx-admin-table">
            <thead>
              <tr><th>Title</th><th>Organization</th><th>Category</th><th>Status</th><th>Featured</th><th>Deadline</th></tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="wfx-admin-table-row-clickable"
                  onClick={() => setSelectedId(row.id)}
                >
                  <td><strong>{row.title}</strong></td>
                  <td>{row.organization}</td>
                  <td>{categoryLabel(row.category)}</td>
                  <td><AdminStatusBadge status={row.status} /></td>
                  <td>{row.isFeatured ? 'Yes' : 'No'}</td>
                  <td>{row.applicationEnd ? new Date(row.applicationEnd).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTablePanel>
      </AdminPageLayout>

      <CreateOpportunityModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        categories={categories}
        qualifications={qualifications}
      />
      <OpportunityDetailCard
        opportunityId={selectedId}
        categories={categories}
        qualifications={qualifications}
        onClose={() => setSelectedId(null)}
      />
    </>
  );
}
